import { Button } from "@/components/ui/button";
import { MapPin, Phone } from "lucide-react";
import {
  parseYouTubeId,
  parseVimeoId,
  type LandingButton,
  type LandingHeroConfig,
} from "@/lib/landingConfig";

interface Props {
  hero: LandingHeroConfig;
  title: string;
  address?: string | null;
  neighborhood: string;
  fallbackImage: string;
  whatsappUrl: string;
}

function resolveHref(btn: LandingButton | undefined, whatsappUrl: string): string {
  if (!btn) return "#";
  switch (btn.action) {
    case "whatsapp": return whatsappUrl;
    case "scroll_cta": return "#cta";
    case "url": return btn.url || "#";
    default: return whatsappUrl;
  }
}

function isExternal(btn?: LandingButton): boolean {
  return btn?.action === "url" || btn?.action === "whatsapp";
}

const LandingHero = ({ hero, title, address, neighborhood, fallbackImage, whatsappUrl }: Props) => {
  const desktop = hero.image_desktop || fallbackImage;
  const mobile = hero.image_mobile || hero.image_desktop || fallbackImage;
  const youtubeId = hero.video_url ? parseYouTubeId(hero.video_url) : null;
  const vimeoId = hero.video_url ? parseVimeoId(hero.video_url) : null;

  const backgroundStyle = hero.background_image
    ? { backgroundImage: `url(${hero.background_image})`, backgroundSize: "cover", backgroundPosition: "center" }
    : undefined;

  const primary = hero.primary_button;
  const secondary = hero.secondary_button;
  const primaryColor = primary?.color;

  return (
    <section className="relative bg-background md:pt-0 pt-20" style={backgroundStyle}>
      {/* Mobile hero */}
      <div className="md:hidden container mx-auto px-4">
        <div className="relative w-full flex justify-center">
          {youtubeId || vimeoId ? (
            <div className="w-full aspect-video">
              <iframe
                src={youtubeId
                  ? `https://www.youtube.com/embed/${youtubeId}`
                  : `https://player.vimeo.com/video/${vimeoId}`}
                title={title}
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                className="w-full h-full rounded"
              />
            </div>
          ) : (
            <img
              src={mobile}
              alt={`${title} - ${neighborhood}`}
              className="w-auto max-w-full h-auto max-h-[85vh] object-contain"
              loading="eager"
            />
          )}
        </div>
      </div>

      {/* Desktop hero: full-bleed image or video with overlay */}
      <div className="hidden md:flex relative min-h-[90vh] items-center justify-start overflow-hidden">
        {youtubeId ? (
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&loop=1&playlist=${youtubeId}&controls=0&modestbranding=1`}
            title={title}
            allow="autoplay; encrypted-media"
            allowFullScreen
            className="absolute inset-0 w-full h-full pointer-events-none"
          />
        ) : vimeoId ? (
          <iframe
            src={`https://player.vimeo.com/video/${vimeoId}?background=1&autoplay=1&muted=1&loop=1`}
            title={title}
            allow="autoplay"
            allowFullScreen
            className="absolute inset-0 w-full h-full pointer-events-none"
          />
        ) : (
          <img
            src={desktop}
            alt={`${title} - ${neighborhood}`}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/60 via-primary/40 to-transparent" />
        <div className="relative z-10 container mx-auto px-4 py-20">
          <div className="max-w-2xl animate-fade-in-up">
            {hero.badge_text && (
              <span className="inline-block rounded bg-accent px-4 py-1.5 text-sm font-semibold text-accent-foreground mb-6 font-body">
                {hero.badge_text}
              </span>
            )}
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 drop-shadow-lg">
              {title}
            </h1>
            {(address || neighborhood) && (
              <p className="flex items-start gap-2 text-white/90 font-body text-lg mb-6 drop-shadow">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 text-accent" />
                <span>{address ? `${address} · ` : ""}{neighborhood}, RJ</span>
              </p>
            )}
            {hero.support_text && (
              <p className="font-body text-white/90 text-base md:text-lg mb-6 drop-shadow max-w-xl">
                {hero.support_text}
              </p>
            )}
            {hero.above_button_text && (
              <p className="font-body text-accent text-sm uppercase tracking-widest mb-3">
                {hero.above_button_text}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-4">
              {primary?.label && (
                <a
                  href={resolveHref(primary, whatsappUrl)}
                  target={isExternal(primary) ? "_blank" : undefined}
                  rel={isExternal(primary) ? "noopener noreferrer" : undefined}
                >
                  <Button
                    variant="gold"
                    size="lg"
                    className="text-lg px-8"
                    style={primaryColor ? { backgroundColor: primaryColor, borderColor: primaryColor } : undefined}
                  >
                    <Phone className="w-5 h-5" />{primary.label}
                  </Button>
                </a>
              )}
              {secondary?.label && (
                <a
                  href={resolveHref(secondary, whatsappUrl)}
                  target={isExternal(secondary) ? "_blank" : undefined}
                  rel={isExternal(secondary) ? "noopener noreferrer" : undefined}
                >
                  <Button variant="heroOutline" size="lg" className="text-lg px-8">
                    {secondary.label}
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingHero;
